import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { UploadIcon } from '@/components/shared/Icons';
import { Drill } from './types/drill-types';
import { 
  validateImportFile, 
  readFileAsText, 
  DrillImportData, 
  ImportMode, 
  isDuplicateDrill
} from './lib';
import logger from '@/lib/logging';
import { useDrillOperations } from './lib/use-drill-operations';

interface DrillImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingDrills: Drill[];
  onImportComplete: (success: number, errors: string[]) => void;
}

const DrillImportModal: React.FC<DrillImportModalProps> = ({
  isOpen,
  onClose,
  existingDrills,
  onImportComplete
}) => {
  const { ops: drillOps } = useDrillOperations();
  const [file, setFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<DrillImportData | null>(null);
  const [importMode, setImportMode] = useState<ImportMode>('skip');
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setError(null);
    
    try {
      const content = await readFileAsText(selectedFile);
      const data = await validateImportFile(content, selectedFile.name);
      
      // Check for duplicates using improved similarity detection
      const duplicates = data.drills.filter(d => 
        existingDrills.some(existing => isDuplicateDrill(d, existing))
      );
      
      setFileData({
        ...data,
        duplicateCount: duplicates.length
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
      setFileData(null);
    }
  }, [existingDrills]);

  const performImport = useCallback(async () => {
    if (!fileData || !file) return;
    
    setIsImporting(true);
    setImportProgress(0);
    
    try {
      const content = await readFileAsText(file);
      const result = await drillOps.importFromFile(
        content,
        file.name,
        importMode === 'skip' ? 'skip' : importMode === 'reimport' ? 'reimport' : 'overwrite',
        (progress) => setImportProgress(progress)
      );
      
      const totalImported = result.success + result.updated;
      onImportComplete(totalImported, result.errors);
    } catch (err) {
      logger.error('Import failed:', err);
      onImportComplete(0, [err instanceof Error ? err.message : 'Unknown error occurred']);
    }
    
    setIsImporting(false);
    handleClose();
  }, [fileData, file, existingDrills, importMode, onImportComplete, drillOps]);

  const handleClose = () => {
    setFile(null);
    setFileData(null);
    setImportMode('skip');
    setIsImporting(false);
    setImportProgress(0);
    setError(null);
    onClose();
  };

  const handleImport = () => {
    performImport();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" aria-describedby="drill-import-description">
        <DialogHeader>
          <DialogTitle>Import Drills</DialogTitle>
          <DialogDescription id="drill-import-description">
            Import drills from a CSV file to add them to your drill library
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* File Upload Section */}
          <div className="space-y-2">
            <Label>Select CSV file to import</Label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="drill-import-file"
                disabled={isImporting}
              />
              <label htmlFor="drill-import-file">
                <Button variant="outline" asChild disabled={isImporting}>
                  <span className="flex items-center gap-2 cursor-pointer">
                    <UploadIcon />
                    Choose File
                  </span>
                </Button>
              </label>
              {file && (
                <span className="text-sm text-muted-foreground">
                  {file.name}
                </span>
              )}
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* File Info Section */}
          {fileData && !error && (
            <>
              <Alert>
                <AlertDescription>
                  <div className="space-y-1">
                    <p>Drills to import: <strong>{fileData.drills.length}</strong></p>
                    {fileData.duplicateCount > 0 && (
                      <p className="text-warning-text">
                        Duplicate drills found: <strong>{fileData.duplicateCount}</strong>
                      </p>
                    )}
                    {fileData.exportedAt && (
                      <p className="text-sm text-muted-foreground">
                        Exported: {new Date(fileData.exportedAt).toLocaleDateString(navigator.language)}
                      </p>
                    )}
                  </div>
                </AlertDescription>
              </Alert>

              {/* Import Mode Selection */}
              <div className="space-y-2">
                <Label>How to handle duplicate drills?</Label>
                <RadioGroup value={importMode} onValueChange={(v) => setImportMode(v as ImportMode)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="skip" id="skip" />
                    <Label htmlFor="skip">Skip duplicates (recommended)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="reimport" id="reimport" />
                    <Label htmlFor="reimport">Complete re-import and re-initialization (delete all existing drills first)</Label>
                  </div>
                </RadioGroup>
                
                {/* Warning for reimport mode */}
                {importMode === 'reimport' && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      <strong>Warning:</strong> This will permanently delete all existing drills before importing the new ones. This action cannot be undone.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </>
          )}

          {/* Import Progress */}
          {isImporting && (
            <div className="space-y-2">
              <Label>Importing drills...</Label>
              <Progress value={importProgress} />
              <p className="text-sm text-muted-foreground">{importProgress}% complete</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} disabled={isImporting}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport}
              disabled={!fileData || isImporting || !!error}
            >
              {isImporting 
                ? 'Importing...' 
                : importMode === 'reimport' 
                  ? `Re-import ${fileData?.drills.length || 0} drills (delete all existing first)`
                  : `Import ${fileData?.drills.length || 0} drills`
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DrillImportModal;
